import React, { useEffect, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import { ButtonOne } from '../../ButtonOne/ButtonOne.component';
import { useStyles } from './VerifierScoreConfirmation.styles';
import { VerificationState } from '../../../pages/Assessment/Assessment.types';
import { getExamTaskGroupSolutionScoreVersions } from '../../../services/scoring/scoring.service';
import { getExamTasksWithCriteria } from '../../../services/exams/assessmentSession/assessments.service';
import { ExamTaskGroupSolutionWithTasks } from '../../../models/assessment.types';
import { FinalScoreAuthor } from '../../../pages/Assessment/Assessment.types';
import { useScoring } from '../../../Context/Scoring.context';

interface VerifierScoreConfirmationProps {
  examinerScore: number | null;
  verifierScore: number | null;
  verificationState: VerificationState | null;
  maxScore: number;
  onConfirm: (useVerifierScore: 'examiner' | 'verifier') => void;
  isDisabled?: boolean;
  examTaskGroupSolutionScoreId: string;
  isVerifierPreview?: boolean;
  isExaminerPreview?: boolean;
}

export const VerifierScoreConfirmation: React.FC<VerifierScoreConfirmationProps> = (props) => {
  const classes = useStyles();
  const {
    examinerScore,
    verifierScore,
    verificationState,
    maxScore,
    isDisabled,
    examTaskGroupSolutionScoreId,
    isVerifierPreview,
    isExaminerPreview,
  } = props;
  const { loadTasks, examTaskGroupSolutionWithTasks } = useScoring();

  const needsVersionFetch =
    verificationState === VerificationState.scoreSelected || verificationState === VerificationState.pendingApproval;
  const [isLoading, setIsLoading] = useState(needsVersionFetch);
  const [selectedScoreBelongsToVerifier, setSelectedScoreBelongsToVerifier] = useState<boolean>(true);

  useEffect(() => {
    if (
      verificationState !== VerificationState.scoreSelected &&
      verificationState !== VerificationState.pendingApproval
    )
      return;
    if (isVerifierPreview || isExaminerPreview) return;

    setIsLoading(true);
    getExamTaskGroupSolutionScoreVersions(examTaskGroupSolutionScoreId).then((response) => {
      const {
        examinerExamTaskGroupSolutionScoreVerionId,
        verifierExamTaskGroupSolutionScoreVerionId,
        selectedScoreBelongsToVerifier: belongsToVerifier,
      } = response.data;
      setSelectedScoreBelongsToVerifier(belongsToVerifier);
      Promise.all([
        getExamTasksWithCriteria(examTaskGroupSolutionScoreId, examinerExamTaskGroupSolutionScoreVerionId),
        getExamTasksWithCriteria(examTaskGroupSolutionScoreId, verifierExamTaskGroupSolutionScoreVerionId),
      ]).then(([examinerData, verifierData]) => {
        const merged: ExamTaskGroupSolutionWithTasks = {
          ...examinerData,
          examTasksCriteria: examinerData.examTasksCriteria.map((task) => {
            const verifierTask = verifierData.examTasksCriteria.find((t) => t.taskId === task.taskId);
            return {
              ...task,
              verifierNoAnswer: verifierTask?.noAnswer,
              examScoringSchemas: task.examScoringSchemas.map((schema) => {
                const verifierSchema = verifierTask?.examScoringSchemas.find(
                  (s) => s.examScoringSchemaId === schema.examScoringSchemaId,
                );
                return { ...schema, verifierScore: verifierSchema?.score ?? null };
              }),
            };
          }),
        };
        loadTasks(merged);
        setIsLoading(false);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examTaskGroupSolutionScoreId, verificationState]);

  const isScoreSelected = verificationState === VerificationState.scoreSelected;

  const scoresMatch =
    examinerScore !== null && verifierScore !== null && verificationState === VerificationState.noChanges;

  const hasDiscrepancy =
    examinerScore !== null &&
    verifierScore !== null &&
    (verificationState === VerificationState.pendingApproval || verificationState === VerificationState.scoreSelected);

  const confirmedSelectedScore: 'examiner' | 'verifier' = selectedScoreBelongsToVerifier ? 'verifier' : 'examiner';

  const [selectedScore, setSelectedScore] = useState<'examiner' | 'verifier' | ''>('');

  const handleConfirm = () => {
    if (!selectedScore) return;
    props.onConfirm(selectedScore);
  };

  if (isLoading && !isVerifierPreview && !isExaminerPreview) return null;

  if (isExaminerPreview || isVerifierPreview) {
    if (verificationState === VerificationState.scoreSelected) {
      const finalScoreAuthor = examTaskGroupSolutionWithTasks?.finalScoreAuthor;
      const effectiveSelectedScore: 'examiner' | 'verifier' =
        finalScoreAuthor === FinalScoreAuthor.Examiner ? 'examiner' : 'verifier';
      return (
        <div className={classes.containerPreview}>
          <FormControl required variant="outlined" size="small" className={classes.selectPreview}>
            <InputLabel>Ocena ostateczna</InputLabel>
            <Select value={effectiveSelectedScore} label="Ocena ostateczna *" disabled>
              <MenuItem value="examiner">Ocena Egzaminatora ({examinerScore ?? '-'})</MenuItem>
              <MenuItem value="verifier">Ocena Weryfikatora ({verifierScore ?? '-'})</MenuItem>
            </Select>
          </FormControl>
        </div>
      );
    }
    return null;
  }

  return (
    <div className={classes.container}>
      <Typography className={classes.scoreRow}>
        Suma pkt egzaminatora:{' '}
        <strong>
          {examinerScore ?? '-'}/{maxScore}
        </strong>
      </Typography>
      <Typography className={classes.scoreRow}>
        Suma pkt weryfikatora:{' '}
        <strong>
          {verificationState === VerificationState.noChanges
            ? examinerScore
            : verifierScore !== null
            ? verifierScore
            : '-'}
          /{maxScore}
        </strong>
      </Typography>
      {hasDiscrepancy && (
        <Typography className={classes.discrepancy}>Wystąpiły rozbieżności w ocenie weryfikatora</Typography>
      )}
      {scoresMatch && <Typography className={classes.noDiscrepancy}>Brak zmian w punktacji</Typography>}
      {!scoresMatch && hasDiscrepancy && verificationState === VerificationState.scoreSelected && (
        <div className={classes.formRow}>
          <FormControl variant="outlined" size="small" className={classes.select}>
            <InputLabel>Ocena ostateczna</InputLabel>
            <Select value={confirmedSelectedScore} label="Ocena ostateczna" disabled>
              <MenuItem value="examiner">Ocena Egzaminatora ({examinerScore ?? '-'})</MenuItem>
              <MenuItem value="verifier">Ocena Weryfikatora ({verifierScore ?? '-'})</MenuItem>
            </Select>
          </FormControl>
        </div>
      )}
      {!scoresMatch && verificationState !== VerificationState.scoreSelected && (
        <div className={classes.formRow}>
          <FormControl variant="outlined" size="small" className={classes.select}>
            <InputLabel shrink style={{ backgroundColor: '#FFFFFF', paddingLeft: 2, paddingRight: 4 }}>
              Ocena ostateczna
            </InputLabel>
            <Select
              value={selectedScore}
              onChange={(e) => setSelectedScore(e.target.value as 'examiner' | 'verifier')}
              displayEmpty
              renderValue={selectedScore !== '' ? undefined : () => 'Wybierz ocenę ostateczną'}
              disabled={isDisabled || isScoreSelected}
            >
              <MenuItem value="examiner">Ocena Egzaminatora ({examinerScore ?? '-'})</MenuItem>
              <MenuItem value="verifier">Ocena Weryfikatora ({verifierScore ?? '-'})</MenuItem>
            </Select>
          </FormControl>
        </div>
      )}
      {!scoresMatch && verificationState !== VerificationState.scoreSelected && (
        <ButtonOne
          variant="contained"
          color="primary"
          className={classes.button}
          disabled={isDisabled || !selectedScore}
          onClick={handleConfirm}
        >
          ZATWIERDŹ
        </ButtonOne>
      )}
    </div>
  );
};
